import { defineStore } from 'pinia';
import { deepCopy, isEqual } from 'commons/utils';
import publishingCoreService from 'commons/api/publishingCoreService/publishingCoreService';
import compositionHelper from 'commons/composition/compositionHelper';
import {
  getHotspotOfDesign,
  withUnplacedDesignsRestored,
} from 'commons/composition/multiPrintAreaCreation';
import compositionValidator from 'commons/api/validators/composition/compositionValidator';
import {
  PUBLISHING_CATEGORY,
  getCompositionMode,
  CompositionMode,
} from '@spreadgroup/common-partner-lib/composition';
import type {
  Composition,
  PublishingCategoryConfig,
  HotspotConfigs,
  AdvancedAssortmentRule,
  AdvancedTemplateProduct,
} from '@spreadgroup/common-partner-lib/composition';
import type { PublishingCoreShop } from 'commons/api/publishingCoreService/publishingCoreService';
import store from 'commons/store/Store';
import { usePublishingStore } from 'commons/stores/publishing';
import { useCompositionsStore } from 'commons/stores/compositions';
import { ExtendedApparelTargetGroupLabel } from '@spreadgroup/common-partner-lib/publishing';
import { CategoriesTree } from 'commons/composition/compositionCategoryHelper';

type State = {
  current: Composition | null;
  original: Composition | null;
  categories: CategoriesTree | null;
};

export const useCompositionStore = defineStore('composition', {
  state: (): State => ({
    current: null,
    original: null,
    categories: null,
  }),

  getters: {
    dirty: (state) => !isEqual(state.current, state.original),
    isPublic: (state) =>
      state.current?.publishingDetails?.d2c?.mp ||
      state.current?.publishingDetails?.spreadShops?.shops?.length,
    shouldValidate(): boolean {
      return !!this.isPublic;
    },
    defaultAppearanceGroup: (state) => {
      if (!state.current) return null;
      return compositionHelper.getDefaultAppearanceGroup(state.current);
    },
    designDownloadLink(state) {
      return `/api/v1/users/${store.state.user.data.id}/design-downloads/${state.current?.mainDesign.id}`;
    },
    mode(state): CompositionMode {
      if (!state.current) return CompositionMode.UNSUPPORTED;
      const mode = getCompositionMode(state.current);
      const publishingStore = usePublishingStore();
      if (publishingStore.isMpScope && mode === CompositionMode.ADVANCED) {
        return CompositionMode.UNSUPPORTED;
      }
      return mode;
    },
  },

  actions: {
    setComposition(composition: Composition) {
      this.current =
        compositionHelper.addEmptyDataStructureForDefaultMetaData(composition);
      this.original = deepCopy(this.current);
    },

    setCompositionCategories(categories: CategoriesTree) {
      this.categories = deepCopy(categories);
    },

    resetToOriginal() {
      this.current = deepCopy(this.original);
    },

    setName(name: string, language?: string) {
      if (!this.current) return;
      const nameMetaData = compositionHelper.getMetaDataPart({
        composition: this.current,
        part: 'name',
        language,
      });

      if (nameMetaData) {
        nameMetaData.value = name;
      }
    },

    setDescription(description: string, language?: string) {
      if (!this.current) return;
      const descriptionMetaData = compositionHelper.getMetaDataPart({
        composition: this.current,
        part: 'description',
        language,
      });

      if (descriptionMetaData) {
        descriptionMetaData.value = description;
      }
    },

    addTag(tag: string, language?: string) {
      if (!this.current) return;
      const tagsMetaData = compositionHelper.getMetaDataPart({
        composition: this.current,
        part: 'tags',
        language,
      });

      if (!tagsMetaData?.values?.includes(tag)) {
        tagsMetaData?.values?.push(tag);
      }
    },

    removeTag(tag: string, language?: string) {
      if (!this.current) return;
      const tagsMetaData = compositionHelper.getMetaDataPart({
        composition: this.current,
        part: 'tags',
        language,
      });
      if (tagsMetaData?.values) {
        tagsMetaData.values = tagsMetaData.values.filter((t) => t !== tag);
      }
    },

    setTags(tags: string[], language?: string) {
      if (!this.current) return;
      const tagsMetaData = compositionHelper.getMetaDataPart({
        composition: this.current,
        part: 'tags',
        language,
      });
      if (tagsMetaData) {
        tagsMetaData.values = tags;
      }
    },

    setMpPublishing(mpState: boolean) {
      if (!this.current) return;
      this.current.publishingDetails.d2c.mp = mpState;
      this.current.publishingDetails.d2c.emp = mpState;
      this.current.publishingDetails.d2c.cyo = mpState;
      this.current.publishingDetails.publish = true;
    },

    publishToShop(shopId: string | number) {
      if (!this.current) return;
      const numericShopId = parseInt(shopId.toString());
      if (
        this.current.publishingDetails.spreadShops.shops.some(
          (shop) => shop.id === numericShopId,
        )
      ) {
        return;
      }
      this.current.publishingDetails.spreadShops.shops.push({
        id: numericShopId,
      });
    },

    unpublishFromShop(shopId: string | number) {
      if (!this.current) return;
      const numericShopId = parseInt(shopId.toString());
      this.current.publishingDetails.spreadShops.shops =
        this.current.publishingDetails.spreadShops.shops.filter(
          (shop) => shop.id !== numericShopId,
        );
    },

    setDefaultLanguage(language: string) {
      if (!this.current) return;
      this.current.metaData.originLanguageCode = language;
      this.current = compositionHelper.resetMetaData(this.current);
    },

    setAutoTranslate(autoTranslate: boolean) {
      if (!this.current) return;
      this.current.metaData.autoTranslate = autoTranslate;
    },

    toggleBackgroundColor() {
      if (!this.current) return;
      this.current.properties.darkBackground =
        !this.current.properties.darkBackground;
    },

    updateCompositionAfterProductSettingsUpdate(
      updatedComposition: Composition,
    ) {
      if (!this.current || !this.original) return;
      const propertiesToCopy = [
        'defaultValues',
        'sellableErrors',
        'sellables',
        'templateProducts',
        'assortmentRule',
        'properties',
      ] as const;

      propertiesToCopy.forEach((property) => {
        if (property in updatedComposition) {
          (this.current as Record<string, unknown>)[property] = deepCopy(
            (updatedComposition as Record<string, unknown>)[property],
          );
          (this.original as Record<string, unknown>)[property] = deepCopy(
            (updatedComposition as Record<string, unknown>)[property],
          );
        }
      });
    },

    setAttributes(attributes: Record<string, string>) {
      if (!this.current) return;
      this.current.attributes = attributes;
    },

    editAttributes(attributes: Record<string, string>) {
      if (!this.current) return;
      this.current.attributes = { ...this.current.attributes, ...attributes };
    },

    async fetchComposition(compositionId: string) {
      const composition =
        await publishingCoreService.getComposition(compositionId);
      this.setComposition(composition);

      this.fetchCompositionCategories(compositionId);
    },

    async fetchCompositionCategories(compositionId: string) {
      const compositionCategories =
        await publishingCoreService.getCompositionCategories(compositionId);
      this.setCompositionCategories(compositionCategories);
    },

    async updateComposition() {
      if (!this.current) return;

      let validation = { result: true };
      if (this.shouldValidate) {
        validation = compositionValidator.validate(this.current);
      }
      if (!validation.result) {
        return Promise.reject(validation);
      }

      const composition = await publishingCoreService.updateComposition(
        this.current,
      );
      this.setComposition(composition);

      useCompositionsStore().replaceComposition(composition);
      store.dispatch('publishingCore/fetchPublishingIntents');
    },

    async updateCompositionProductSettings({
      hotspotConfigs,
      defaultHotspot,
      defaultAppearanceGroup,
      categories,
      labels,
    }: {
      hotspotConfigs: HotspotConfigs;
      defaultHotspot: string;
      defaultAppearanceGroup?: string;
      categories: PublishingCategoryConfig[];
      labels: ExtendedApparelTargetGroupLabel[];
    }) {
      if (!this.original) return;

      const composition = await publishingCoreService.updateComposition({
        id: this.original.id,
        templateProducts: [
          {
            templateProduct: {
              defaultHotspot,
              hotspotConfigs,
              appearanceRestriction: defaultAppearanceGroup
                ? {
                    includedAppearanceGroups: [defaultAppearanceGroup],
                  }
                : undefined,
            },
          },
        ],
        assortmentRule: compositionHelper.transformCategoryListToAssortmentRule(
          {
            categories,
            labelsForCategory: {
              [PUBLISHING_CATEGORY.APPAREL.id as string]: labels,
            },
          },
        ),
      });
      this.updateCompositionAfterProductSettingsUpdate(composition);
    },

    publishToAllShops() {
      store.state.publishingCore.shops.forEach((shop: PublishingCoreShop) =>
        this.publishToShop(shop.shopId),
      );
    },

    unpublishFromAllShops() {
      store.state.publishingCore.shops.forEach((shop: PublishingCoreShop) =>
        this.unpublishFromShop(shop.shopId),
      );
    },

    setPublishedShops(shopIds: (string | number)[]) {
      if (!this.current) return;

      const newShopIds = shopIds.map((shopId) => parseInt(shopId.toString()));
      this.current.publishingDetails.spreadShops.shops
        .filter((shop) => !newShopIds.includes(shop.id))
        .forEach((shop) => this.unpublishFromShop(shop.id));

      newShopIds.forEach((shopId) => this.publishToShop(shopId));
    },

    async addOrUpdateProductConfig(
      productTypeId: number,
      defaultAppearanceId: number,
      activatedAppearanceIds: number[],
      hotspotConfig: HotspotConfigs,
    ) {
      if (!this.current) return;

      // set assortment rule
      if (
        this.current.assortmentRule?.baseLabelTerm?.productTypes &&
        !this.current.assortmentRule.baseLabelTerm.productTypes.includes(
          productTypeId,
        )
      ) {
        this.current.assortmentRule.baseLabelTerm.productTypes.push(
          productTypeId,
        );
      }

      const advancedAssortmentRule = this.current
        .assortmentRule as AdvancedAssortmentRule;
      if (!advancedAssortmentRule?.appearanceRules) {
        advancedAssortmentRule!.appearanceRules = [];
      }

      const existingAppearanceRuleIndex =
        advancedAssortmentRule.appearanceRules.findIndex(
          (rule) => rule.productTypeId === productTypeId,
        );

      if (existingAppearanceRuleIndex >= 0) {
        advancedAssortmentRule.appearanceRules[existingAppearanceRuleIndex] = {
          productTypeId,
          includedAppearanceIds: activatedAppearanceIds,
        };
      } else {
        advancedAssortmentRule.appearanceRules.push({
          productTypeId,
          includedAppearanceIds: activatedAppearanceIds,
        });
      }

      // set template product
      const advancedTemplateProducts = this.current
        .templateProducts as AdvancedTemplateProduct[];

      const existingTemplateProductIndex = advancedTemplateProducts.findIndex(
        (tp) => tp.productTypeIds?.includes(productTypeId),
      );

      const templateProductData = {
        productTypeIds: [productTypeId],
        templateProduct: {
          // Must be the main design's hotspot, not whichever key happens to come first — see
          // `docs/publishing/composition-mode-contract.md` §7.3.1.
          defaultHotspot:
            getHotspotOfDesign(hotspotConfig, this.current.mainDesign.id) ||
            Object.keys(hotspotConfig)[0],
          hotspotConfigs: hotspotConfig,
          appearanceRestriction: {
            includedAppearanceIds: [defaultAppearanceId],
          },
        },
      };

      if (existingTemplateProductIndex >= 0) {
        advancedTemplateProducts[existingTemplateProductIndex] =
          templateProductData;
      } else {
        advancedTemplateProducts.push(templateProductData);
      }

      const composition = await publishingCoreService.updateComposition({
        id: this.current.id,
        templateProducts: this.current.templateProducts,
        assortmentRule: this.current.assortmentRule,
      });

      this.updateModeRelevantProperties(composition);
    },

    async deleteProductConfigs(productTypeIds: number[]) {
      if (!this.current) return;

      // clean up assortment rule
      const advancedAssortmentRule = this.current
        .assortmentRule as AdvancedAssortmentRule;
      if (advancedAssortmentRule?.baseLabelTerm?.productTypes) {
        advancedAssortmentRule.baseLabelTerm.productTypes =
          advancedAssortmentRule.baseLabelTerm.productTypes.filter(
            (id) => !productTypeIds.includes(id),
          );
      }

      if (advancedAssortmentRule?.appearanceRules) {
        advancedAssortmentRule.appearanceRules =
          advancedAssortmentRule.appearanceRules.filter(
            (rule) => !productTypeIds.includes(rule.productTypeId),
          );
      }

      // clean up template products
      const advancedTemplateProducts = this.current
        .templateProducts as AdvancedTemplateProduct[];
      this.current.templateProducts = advancedTemplateProducts.filter(
        (tp) => !tp.productTypeIds?.some((id) => productTypeIds.includes(id)),
      );

      const composition = await publishingCoreService.updateComposition({
        id: this.current.id,
        templateProducts: this.current.templateProducts,
        assortmentRule: this.current.assortmentRule,
      });

      this.updateModeRelevantProperties(composition);
    },

    updateModeRelevantProperties(composition: Composition) {
      if (!this.current || !this.original) return;

      if (composition.templateProducts) {
        this.current.templateProducts = deepCopy(composition.templateProducts);
        this.original.templateProducts = deepCopy(composition.templateProducts);
      }

      if (composition.assortmentRule) {
        this.current.assortmentRule = deepCopy(composition.assortmentRule);
        this.original.assortmentRule = deepCopy(composition.assortmentRule);
      }

      if (composition.sellables) {
        this.current.sellables = deepCopy(composition.sellables);
        this.original.sellables = deepCopy(composition.sellables);
      }

      if (composition.defaultValues) {
        this.current.defaultValues = deepCopy(composition.defaultValues);
        this.original.defaultValues = deepCopy(composition.defaultValues);
      }

      if (composition.sellableErrors) {
        this.current.sellableErrors = deepCopy(composition.sellableErrors);
        this.original.sellableErrors = deepCopy(composition.sellableErrors);
      }
    },

    async resetToMode(mode: CompositionMode) {
      if (!this.current || !this.original) return;

      if (mode === CompositionMode.SMART) {
        // The reset strands every design but the main one — see `withUnplacedDesignsRestored`.
        const reset = await publishingCoreService.getResetComposition(
          this.current.id,
        );
        const composition = await publishingCoreService.updateComposition(
          withUnplacedDesignsRestored(reset),
        );

        this.updateModeRelevantProperties(composition);
      } else if (mode === CompositionMode.ADVANCED) {
        const composition = await publishingCoreService.updateComposition({
          id: this.current.id,
          templateProducts: [],
          assortmentRule: {
            baseLabelTerm: {
              productTypes: [],
              exclude: false,
            },
          },
        });

        this.updateModeRelevantProperties(composition);
      }
    },
  },
});
